import { LabResultsOverview } from "./LabResultsOverview";
import { labRecords } from "./labBiomarkers";
import { LabPreviewControls } from "./LabPreviewControls";
import { LabHowItWorks } from "./LabHowItWorks";
import {
  useLabsExperiment,
  useLabView,
  useLabEvent,
} from "@/features/labs/labExperiment";
import { ArrowRight, FlaskConical } from "lucide-react";
import { useEffect } from "react";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PageHeader } from "@/components/app-shell";
import { SanityImage } from "@/components/content/SanityImage";
import { Badge, Button } from "@/design-system/primitives";
import { useLabOrder, useLabOrders } from "@/lib/api/labHooks";
import { LabKitProgress } from "./LabKitProgress";
import { LabError, LabOrderContent, LabSkeleton } from "./LabOrderContent";
import {
  labDate,
  labDisplayName,
  labHasResults,
  labIsArchived,
  labNeedsAttention,
  labOrderLabel,
  labProductImage,
} from "./labPresentation";

export function LabOrdersSummary() {
  const { enabled: labsEnabled } = useLabsExperiment();
  const query = useLabOrders();
  const [params] = useSearchParams();
  const purchasedId =
    params.get("purchased") === "1" ? params.get("lab_order") : null;
  if (!query.data?.orders.length) return null;
  return (
    <section className="ph-no-capture border border-line-subtle">
      <div className="p-15 bg-raised border-b border-line-subtle flex items-center justify-between gap-10">
        <h2 className="type-label-sm">Labs</h2>
        {labsEnabled && (
          <Link
            className="text-14 underline min-h-40 inline-flex items-center"
            to="/app/labs"
          >
            View all
          </Link>
        )}
      </div>
      {query.data.orders.map((order) => (
        <Link
          key={order.id}
          aria-current={purchasedId === order.id ? "true" : undefined}
          to={`/app/labs/${order.id}`}
          className="aria-[current=true]:bg-accent-wash flex items-center justify-between gap-15 p-20 border-b last:border-b-0 border-line-subtle hover:bg-raised"
        >
          {labProductImage(order.panel_code) && (
            <SanityImage
              url={labProductImage(order.panel_code)}
              alt="System Labs at-home blood collection device"
              width={160}
              height={160}
              className="h-80 w-80 shrink-0 object-contain mix-blend-multiply"
            />
          )}
          <div className="min-w-0 flex-1">
            {purchasedId === order.id && order.payment_status === "paid" && (
              <p role="status" className="type-label-xs text-success mb-8">
                Purchase confirmed
              </p>
            )}
            <h3 className="font-display text-18">
              {labDisplayName(order.product_name)}
            </h3>
            <p className="type-body-sm text-ink-secondary mt-8">
              {labOrderLabel(order)}
            </p>
          </div>
          <ArrowRight aria-hidden size={16} />
        </Link>
      ))}
    </section>
  );
}

export function LabOrdersScreen() {
  const { enabled } = useLabsExperiment();
  useLabView("blood_test_labs_viewed", "labs_tab", enabled);
  const query = useLabOrders();
  const hasResults = Boolean(
    query.data?.orders.some((order) => order.results.length),
  );
  const recent = [...(query.data?.orders ?? [])].sort(
    (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
  );
  const groups = [
    {
      title: "Current tests",
      orders: recent.filter(
        (order) =>
          !labIsArchived(order) &&
          (!labHasResults(order) || labNeedsAttention(order)),
      ),
    },
    {
      title: "Results and history",
      orders: recent.filter(
        (order) =>
          labIsArchived(order) ||
          (labHasResults(order) && !labNeedsAttention(order)),
      ),
    },
  ];
  return (
    <div>
      <PageHeader
        title="Labs"
        description="Follow your kit from delivery to the laboratory, then review your results here."
      />
      <div className="ph-no-capture px-15 md:px-40 py-25 max-w-[120rem] mx-auto flex flex-col gap-20">
        <LabPreviewControls />
        <div className="flex items-center justify-between gap-15">
          <LabHowItWorks source="labs_tab" />
          {/* The Shop's labs tab exists only in the Labs arm; members outside
              it reach this list for their own tests. */}
          {enabled && (
            <Button asChild variant="secondary">
              <Link to="/app/shop?tab=labs">
                Explore lab tests <ArrowRight size={14} />
              </Link>
            </Button>
          )}
        </div>
        {hasResults && <LabResultsOverview orders={recent} />}
        {query.isLoading ? (
          <LabSkeleton label="Fetching your blood tests" />
        ) : query.isError ? (
          <LabError
            message="We could not retrieve your blood tests."
            onRetry={() => void query.refetch()}
          />
        ) : !query.data?.orders.length ? (
          <section className="border border-line-subtle p-30">
            <FlaskConical size={28} aria-hidden className="text-accent mb-20" />
            <h2 className="font-display text-24">No blood tests yet.</h2>
            <p className="type-body-md text-ink-secondary mt-12 leading-145">
              Once you order a test, its kit tracking and results will appear
              here.
            </p>
            {enabled && (
              <Button asChild className="mt-20">
                <Link to="/app/shop?tab=labs">
                  Explore lab tests <ArrowRight size={16} />
                </Link>
              </Button>
            )}
          </section>
        ) : (
          groups
            .filter((group) => group.orders.length > 0)
            .map((group) => (
              <section
                key={group.title}
                aria-label={group.title}
                className="flex flex-col gap-15"
              >
                <h2 className="type-label-md">{group.title}</h2>
                {group.orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/app/labs/${encodeURIComponent(order.id)}`}
                    className="border border-line-subtle p-20 sm:p-25 hover:bg-raised flex flex-col gap-20"
                  >
                    <div className="flex items-center gap-15">
                      <img
                        src="/images/tasso/system-collection-device-transparent.png"
                        alt="System at-home blood collection device"
                        width={720}
                        height={960}
                        className="h-80 w-60 shrink-0 object-contain"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="type-label-xs text-ink-muted">
                          {labDate(order.created_at)}
                        </p>
                        <h3 className="font-display text-24 mt-10">
                          {labDisplayName(order.product_name)}
                        </h3>
                        <div className="mt-12">
                          <Badge
                            tone={
                              labNeedsAttention(order) ? "warning" : "neutral"
                            }
                          >
                            {labOrderLabel(order)}
                          </Badge>
                        </div>
                      </div>
                      <ArrowRight size={20} aria-hidden className="shrink-0" />
                    </div>
                    {!labIsArchived(order) && !labHasResults(order) && (
                      <LabKitProgress order={order} />
                    )}
                  </Link>
                ))}
              </section>
            ))
        )}
        {!!query.data?.orders.length && <LabAccountLink />}
      </div>
    </div>
  );
}

export function LabOrderScreen() {
  const emit = useLabEvent();
  const { enabled: labsEnabled } = useLabsExperiment();
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const query = useLabOrder(orderId);
  const history = useLabOrders(Boolean(query.data?.results.length));
  const initialMarker = location.state?.labMarker;
  useLabView("blood_test_order_viewed", "lab_order", Boolean(query.data));
  useEffect(() => {
    if (location.hash === "#results" && query.data) {
      document.getElementById("results")?.scrollIntoView?.({ block: "start" });
    }
  }, [location.key, location.hash, Boolean(query.data)]);
  return (
    <div>
      <PageHeader
        title="Your blood test"
        description="Your kit, laboratory updates, and original report in one place."
      />
      <div className="px-15 md:px-40 py-25 max-w-[120rem] mx-auto flex flex-col gap-20">
        <LabPreviewControls />
        <div className="flex items-center justify-between gap-10">
          <Link
            to={labsEnabled ? "/app/labs" : "/app/orders"}
            className="text-14 underline min-h-40 inline-flex items-center"
          >
            {labsEnabled ? "All Labs" : "All orders"}
          </Link>
          <Button
            variant="ghost"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        </div>
        {params.get("purchased") === "1" && query.data && (
          <p
            role="status"
            className="border border-line-subtle p-15 type-body-md"
          >
            Your payment is confirmed. We’re preparing your kit for delivery.
          </p>
        )}
        {query.isLoading ? (
          <LabSkeleton />
        ) : query.isError || !query.data ? (
          <LabError
            message="We could not open this blood test. It may not be available for your account."
            onRetry={() => void query.refetch()}
          />
        ) : (
          <LabOrderContent
            key={query.data.id}
            order={query.data}
            records={labRecords(history.data?.orders ?? [])}
            initialMarker={
              initialMarker &&
              typeof initialMarker.resultId === "string" &&
              Number.isInteger(initialMarker.index)
                ? initialMarker
                : undefined
            }
            onReportOpen={() =>
              emit("blood_test_report_opened", { source: "lab_order" })
            }
          />
        )}
        {query.data && <LabAccountLink />}
      </div>
    </div>
  );
}

function LabAccountLink() {
  return (
    <Link
      to="/app/account?focus=subscriptions"
      className="inline-flex items-center gap-8 min-h-44 text-14 underline self-start"
    >
      Manage subscriptions in Account <ArrowRight size={14} aria-hidden />
    </Link>
  );
}
